import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { SmsTopKpis } from './components/SmsTopKpis';
import { SmsManagementCard } from './components/SmsManagementCard';
import { SmsApiSettingsModal } from './components/SmsApiSettingsModal';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import { useMe } from 'core/me/provider/MeProvider';
import { smsApi } from './api/smsApi';

/* ---------------- ViewModel ---------------- */
function buildsmsViewModel(companyDto, settingsDto) {
  const moduleEnabled = Boolean(companyDto?.moduleEnabled);

  const smsMode = companyDto?.smsMode || 'SOLISUP_MANAGED';

  const used = Number(companyDto?.quotaUsed ?? 0);

  const unlimited = companyDto?.quotaLimit == null; // null => unlimited
  const quotaLimit = unlimited ? null : Number(companyDto.quotaLimit);

  const remaining = unlimited
    ? null
    : companyDto?.quotaRemaining == null
    ? Math.max(0, (quotaLimit ?? 0) - used)
    : Number(companyDto.quotaRemaining);

  const gateNoQuota = !unlimited && moduleEnabled && (remaining ?? 0) <= 0;
  const gateClosed = !moduleEnabled;

  const provider = companyDto?.smsProvider || null;

  const topluRoot = companyDto?.topluUserInfoRaw || null;
  const topluData = topluRoot?.data || null;
  const topluCredit = typeof topluData?.credit === 'number' ? topluData.credit : null;

  const vatanRoot = companyDto?.vatanUserInfoRaw || null;
  const vatanData = vatanRoot?.data || null;
  const vatanCredit = typeof vatanData?.credit === 'number' ? vatanData.credit : null;

  const providerInfo =
    provider === 'TOPLUSMS'
      ? {
          name: 'TOPLUSMS',
          ok: Boolean(companyDto?.topluUserInfoOk),
          error: companyDto?.topluUserInfoError ?? null,
          data: topluData,
          credit: topluCredit,
          sender: companyDto?.topluSender ?? '',
        }
      : {
          name: 'VATANSMS',
          ok: Boolean(companyDto?.vatanUserInfoOk),
          error: companyDto?.vatanUserInfoError ?? null,
          data: vatanData,
          credit: vatanCredit,
          sender: companyDto?.vatanSender ?? '',
        };
  return {
    moduleEnabled,
    smsMode,
    provider,
    providerInfo,
    hasVatanApiKey: Boolean(companyDto?.hasVatanApiKey),
    vatanSender: companyDto?.vatanSender ?? '',
    vatanMessageType: companyDto?.vatanMessageType ?? 'normal',
    vatanMessageContentType: companyDto?.vatanMessageContentType ?? 'bilgi',
    vatanAddCancelLink: companyDto?.vatanAddCancelLink ?? true,

    enabled: Boolean(settingsDto?.enabled),

    quotaLimit,
    used,
    remaining,

    periodStartAt: companyDto?.periodStart ?? null,
    periodEndAt: companyDto?.periodEnd ?? null,

    gate: { closed: gateClosed, noQuota: gateNoQuota },

    attendanceReminder: settingsDto?.attendanceReminder || {},
    attendanceInquiryPage: settingsDto?.attendanceInquiryPage || {},
    paidSessionInfo: settingsDto?.paidSessionInfo || {},
    birthday: settingsDto?.birthday || {},

    // ✅ Vatan raw info
    vatanUserInfoOk: Boolean(companyDto?.vatanUserInfoOk),
    vatanUserInfoError: companyDto?.vatanUserInfoError ?? null,
    vatanUserInfoRaw: vatanRoot,
    vatanUserInfoData: vatanData,
    vatanCredit,

    rawCompany: companyDto,
    rawSettings: settingsDto,
  };
}
function InfoCell({ label, value, icon }) {
  return (
    <MDBox
      sx={{
        p: 1.25,
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
        <Icon fontSize="small" sx={{ opacity: 0.75 }}>
          {icon}
        </Icon>
        <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
          {label}
        </MDTypography>
      </MDBox>

      <MDTypography variant="button" fontWeight="bold">
        {value ? String(value) : '—'}
      </MDTypography>
    </MDBox>
  );
}
export default function SmsManagementPage() {
  // ✅ Company + Settings state
  const [smsCompany, setSmsCompany] = useState(null);
  const [smsSettings, setSmsSettings] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ✅ API modal
  const [apiModalOpen, setApiModalOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const company = await smsApi.getCompany();
      setSmsCompany(company);

      const settings = await smsApi.getSettings();
      setSmsSettings(settings);
    } catch (e) {
      setError(e?.message || 'sms bilgileri alınamadı.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const sms = useMemo(() => buildsmsViewModel(smsCompany, smsSettings), [smsCompany, smsSettings]);

  // ✅ Talep modal state
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestSending, setRequestSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState('');

  const { me } = useMe();
  const fullName = me?.fullName || 'Değerli Kullanıcımız';

  const closeRequestModal = useCallback(() => {
    if (requestSending) return;
    setRequestOpen(false);
  }, [requestSending]);

  const handleRequestClick = useCallback(async () => {
    setRequestOpen(true);
    setRequestSending(true);
    setRequestSent(false);
    setRequestError('');

    try {
      await smsApi.requestEnable();
      setRequestSent(true);
      await loadAll();
    } catch (e) {
      setRequestError('Talep iletilemedi. Lütfen tekrar deneyin.');
    } finally {
      setRequestSending(false);
    }
  }, [loadAll]);

  // ✅ API Kaydet handler (modal burayı çağırıyor)
  const handleSaveApi = useCallback(
    async (payload) => {
      await smsApi.updateCompany(payload);
      await loadAll();
    },
    [loadAll]
  );

  // ---------- Loading / Error shells ----------
  const hardLoading = loading && !smsCompany;
  if (hardLoading) {
    return (
      <MDBox py={3}>
        <MDTypography variant="h5" fontWeight="bold">
          Sms Yönetimi
        </MDTypography>
        <MDBox mt={2}>
          <Skeleton height={120} />
          <Skeleton height={120} sx={{ mt: 2 }} />
        </MDBox>
      </MDBox>
    );
  }

  if (!smsCompany && error) {
    return (
      <MDBox py={3}>
        <MDTypography variant="h5" fontWeight="bold">
          Sms Yönetimi
        </MDTypography>
        <MDBox mt={2}>
          <Alert severity="error">
            sms bilgileri yüklenemedi.
            <MDButton color="info" size="small" onClick={loadAll} sx={{ ml: 1 }}>
              Tekrar dene
            </MDButton>
          </Alert>
        </MDBox>
      </MDBox>
    );
  }
  const providerActive =
    sms?.smsMode === 'COMPANY_PROVIDER' &&
    sms?.moduleEnabled &&
    sms?.providerInfo?.ok &&
    sms?.providerInfo?.data;
  // ---------- Header values ----------
  const enabled = !!sms?.moduleEnabled;

  const headerRightChipLabel = (() => {
    if (sms?.smsMode === 'COMPANY_PROVIDER') {
      const credit = sms?.providerInfo?.credit;
      return `Kredi: ${typeof credit === 'number' ? credit : '—'}`;
    }
    const remaining = Number.isFinite(sms?.remaining) ? sms.remaining : 0;
    const quotaLimit = Number.isFinite(sms?.quotaLimit) ? sms.quotaLimit : 0;
    return `Kalan: ${remaining}/${quotaLimit}`;
  })();

  return (
    <MDBox py={3}>
      {/* ---------- Page Header Row ---------- */}
      <MDBox
        mb={2}
        display="flex"
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={1.5}
      >
        {/* Left: Title */}
        <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
            Sms Yönetimi
          </MDTypography>
          <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
            Otomasyonlar, kota ve son gönderimler
          </MDTypography>
        </MDBox>

        {/* Right: Actions */}
        <MDBox
          display="flex"
          gap={1}
          alignItems="center"
          justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          flexWrap="wrap"
        >
          <Chip
            size="small"
            variant="outlined"
            color={enabled ? 'success' : 'default'}
            label={enabled ? 'Aktif Paket' : 'Paket Yok'}
          />
          <Chip size="small" variant="outlined" label={headerRightChipLabel} />

          <MDButton
            variant="outlined"
            color="info"
            onClick={loadAll}
            startIcon={<Icon>refresh</Icon>}
            disabled={loading}
            sx={{ minWidth: { xs: '48%', sm: 'auto' } }}
          >
            Yenile
          </MDButton>

          <MDButton
            variant="gradient"
            color="dark"
            onClick={() => setApiModalOpen(true)}
            startIcon={<Icon>vpn_key</Icon>}
            disabled={loading}
            sx={{ minWidth: { xs: '48%', sm: 'auto' } }}
          >
            API Bilgileri
          </MDButton>
        </MDBox>
      </MDBox>

      {/* ---------- VATAN Account Card (Responsive) ---------- */}
      {providerActive ? (
        <Card sx={{ mb: 2 }}>
          <MDBox p={{ xs: 1.5, md: 2 }}>
            <MDBox
              display="flex"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
            >
              <MDBox>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small">verified</Icon>
                  <MDTypography variant="h6" fontWeight="bold">
                    SMS Bağlı
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<Icon>check_circle</Icon>}
                  label="Doğrulandı"
                />
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<Icon>sms</Icon>}
                  label={`Kredi: ${
                    typeof sms?.providerInfo?.credit === 'number' ? sms.providerInfo.credit : '—'
                  }`}
                />
              </MDBox>
            </MDBox>

            <Divider sx={{ my: 2 }} />

            <MDBox
              display="grid"
              gridTemplateColumns={{
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr',
              }}
              gap={1.5}
            >
              <InfoCell
                label="Şirket"
                value={sms?.providerInfo?.data?.company_name}
                icon="business"
              />
              <InfoCell label="Kullanıcı" value={sms?.providerInfo?.data?.username} icon="person" />
              <InfoCell label="Sender" value={sms?.providerInfo?.sender} icon="campaign" />
              <InfoCell label="Telefon" value={sms?.providerInfo?.data?.phone} icon="call" />
              <InfoCell label="Email" value={sms?.providerInfo?.data?.email} icon="mail" />
              <InfoCell
                label="OTP Zorunlu"
                value={
                  typeof sms?.providerInfo?.data?.require_login_otp === 'boolean'
                    ? sms.providerInfo.data.require_login_otp
                      ? 'Evet'
                      : 'Hayır'
                    : '—'
                }
                icon="shield"
              />
            </MDBox>

            {sms?.providerInfo?.error ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Kullanıcı bilgisi okunurken uyarı: {sms.providerInfo.error}
              </Alert>
            ) : null}
          </MDBox>
        </Card>
      ) : null}

      {/* Gate banners */}
      {sms?.gate?.closed && (
        <Alert
          severity="warning"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          action={
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={handleRequestClick}
              startIcon={<Icon>support_agent</Icon>}
              disabled={requestSending}
            >
              Talep Et
            </MDButton>
          }
        >
          sms paketi/modülü <b>aktif değil</b>. Açmak için talepte bulunun.
        </Alert>
      )}

      {sms?.gate?.noQuota && (
        <Alert severity="error" sx={{ mb: 2 }}>
          sms mesaj hakkınız <b>kalmamış</b>. Kota ekleyin veya paketinizi yükseltin.
        </Alert>
      )}

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {sms?.smsMode !== 'COMPANY_PROVIDER' && <SmsTopKpis sms={sms} />}
      <MDBox mt={2}>
        <SmsManagementCard sms={sms} refreshSettings={loadAll} />
      </MDBox>

      {/* ✅ Talep Modal */}
      <Dialog open={requestOpen} onClose={closeRequestModal} maxWidth="xs" fullWidth>
        <DialogTitle>sms Modülü Talebi</DialogTitle>

        <DialogContent dividers>
          {requestSending ? (
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <CircularProgress size={18} />
              <MDTypography variant="button" color="text" sx={{ opacity: 0.85 }}>
                Talebiniz iletiliyor…
              </MDTypography>
            </MDBox>
          ) : requestError ? (
            <Alert severity="error">{requestError}</Alert>
          ) : requestSent ? (
            <Alert severity="success">
              <b>Sayın {fullName},</b> talebiniz alındı. En kısa sürede sizinle iletişime geçeceğiz.
            </Alert>
          ) : (
            <MDTypography variant="button" color="text" sx={{ opacity: 0.85 }}>
              Talebiniz iletildiğinde bilgilendirileceksiniz.
            </MDTypography>
          )}
        </DialogContent>

        <DialogActions>
          {requestError ? (
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleRequestClick}
              startIcon={<Icon>refresh</Icon>}
              disabled={requestSending}
            >
              Tekrar Dene
            </MDButton>
          ) : null}

          <MDButton variant="text" color="secondary" onClick={closeRequestModal}>
            Kapat
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✅ API Modal (modüler) */}
      <SmsApiSettingsModal
        open={apiModalOpen}
        onClose={() => setApiModalOpen(false)}
        smsCompany={smsCompany}
        onSave={handleSaveApi}
      />
    </MDBox>
  );
}
